/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import EventEmitter from 'eventemitter3';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import isNil from 'lodash/isNil';
import isObject from 'lodash/isObject';
import sortBy from 'lodash/sortBy';

import Numeric from '../numeric/numeric';

import ScheduleInfo from './schedule-info';
import ScheduleTrack from './schedule-track';

/**
 * Strategie přehrávání pro lineární playlisty.
 * Jde o pomocnou třídu pro playlisty, které přehrávají všechny stopy v rámci jednoho cyklu.
 * Umožňuje zvolit způsob seřazení stop, který je pak stejný pro všechny přehrávací cykly.
 * @implements {ScheduleStrategy}
 */
class ScheduleLinear {
  /**
   * Vytvoří instanci.
   * @param {DS} store Datový sklad.
   * @param {Session} session Sezení s nastavením zařízení.
   * @param {Playlist} playlist Playlist pro aplikaci strategie.
   * @param {Array.<Track>} trackArray Seznam stop playlistu pro aplikaci strategie. Pořadí stop je při plánování dodrženo.
   * @param {ViewerRegistry} viewerRegistry Registr informací o typech médií.
   * @returns {ScheduleLinear} Nová instance.
   */
  constructor(store, session, playlist, trackArray, viewerRegistry) {
    if (!isObject(store)) {
      throw new TypeError(`Invalid store "${store}".`);
    }
    if (!isObject(playlist)) {
      throw new TypeError(`Invalid playlist "${playlist}".`);
    }
    if (!isArray(trackArray)) {
      throw new TypeError(`Invalid track array "${trackArray}".`);
    }
    const scheduleEdgeArray = trackArray.map((track, index) => {
      const scheduleStrategy = new ScheduleTrack(
        store,
        session,
        track,
        viewerRegistry
      );
      return {
        // Navázání naslouchání na instanci.
        onPlayed: this.onPlayed.bind(this, scheduleStrategy),
        scheduleStrategy
      };
    });
    /**
     * Správce událostí.
     * @private
     * @type {EventEmitter}
     */
    this.emitter = new EventEmitter();
    this.playlist = playlist;
    this.scheduleEdgeArray = scheduleEdgeArray;
    this.schedulePlan = null;
    this.store = store;
    // Naslouchání událostem.
    scheduleEdgeArray.forEach((scheduleEdge) => {
      scheduleEdge.scheduleStrategy.on('played', scheduleEdge.onPlayed);
    });
  }

  /** @inheritdoc */
  destroy() {
    this.scheduleEdgeArray.forEach((scheduleEdge) => {
      const { scheduleStrategy } = scheduleEdge;
      scheduleStrategy.off('played', scheduleEdge.onPlayed);
      scheduleStrategy.destroy();
    });
  }

  /**
   * Najde pořadí hrany v seznamu hran této strategie.
   * @param {ScheduleEdge} scheduleEdge Hrana, pro kterou se má určit pořadí.
   * @private
   * @returns {number} Pořadí hrany.
   */
  findEdgeIndex(scheduleEdge) {
    // Určení indexu aktuální stopy z výchozí.
    const scheduleEdgeIndex = this.scheduleEdgeArray.indexOf(scheduleEdge);
    if (scheduleEdgeIndex === -1) {
      throw new RangeError(
        `Playlist does not contain ${scheduleEdge.scheduleStrategy}.`
      );
    }
    return scheduleEdgeIndex;
  }

  /** @inheritdoc */
  go() {
    let toSchedulePlan;
    const fromSchedulePlan = this.schedulePlan;
    if (isNil(fromSchedulePlan)) {
      // Pokud není nic naplánováno, přejde se na následující (= první) slajd na této úrovni.
      toSchedulePlan = this.goNext(fromSchedulePlan);
    } else {
      // Pokud je přítomný plán, tak se rozhodne dle jeho následujícího slajdu.
      const { scheduleStrategy } = fromSchedulePlan.scheduleEdge;
      const childScheduleInfo = scheduleStrategy.plan(1)[0];
      // Detekce přeskočení na další stopu na této úrovni.
      // Buď pokud má aktuální stopa prázdný obsah.
      // Nebo pokud stopa přehrála všechny slajdy v aktuálním cyklu.
      if (
        childScheduleInfo.isEmpty() ||
        childScheduleInfo.getCycleIndex() > fromSchedulePlan.cycleIndex
      ) {
        // Zastaví se všechny časovače v opouštěné větvi.
        scheduleStrategy.stop();
        // Přejde se na další stopu v aktuální úrovni.
        toSchedulePlan = this.goNext(fromSchedulePlan);
      } else {
        // Přejde se na další stopu ve vnitřním playlistu.
        toSchedulePlan = this.goDeep(fromSchedulePlan);
      }
    }
    // TODO #138 Přepracovat přístup k prázdnému `SchedulePlan`.
    const toScheduleInfo = get(
      toSchedulePlan,
      'scheduleInfo',
      new ScheduleInfo()
    );
    this.schedulePlan = toSchedulePlan;
    // Vyvolání události a vrácení výsledku.
    this.emitter.emit('gone', toScheduleInfo);
    return toScheduleInfo;
  }

  /**
   * Přechod na další plán ve vnitřním playlistu.
   * @param {SchedulePlan} fromSchedulePlan Plán, ze kterého se vychází.
   * @private
   * @returns {SchedulePlan} Plán, na který se má provést přechod.
   */
  goDeep(fromSchedulePlan) {
    const fromCycleIndex = fromSchedulePlan.cycleIndex;
    const fromIterationIndex = fromSchedulePlan.iterationIndex;
    const fromScheduleEdge = fromSchedulePlan.scheduleEdge;
    const childScheduleInfo = fromScheduleEdge.scheduleStrategy.go();
    const toIterationIndex = this.nextIterationIndex(
      fromCycleIndex,
      fromIterationIndex,
      fromCycleIndex
    );
    return {
      cycleIndex: fromCycleIndex,
      iterationIndex: toIterationIndex,
      scheduleEdge: fromScheduleEdge,
      scheduleInfo: childScheduleInfo.merge({
        cycleIndex: fromCycleIndex,
        iterationIndex: toIterationIndex,
        playlistArray: [this.playlist]
      })
    };
  }

  /**
   * Přechod na další plán v aktuální úrovni.
   * @param {SchedulePlan} [fromSchedulePlan] Plán, ze kterého se vychází.
   * @private
   * @returns {SchedulePlan|null} Plán, na který se má provést přechod.
   */
  goNext(fromSchedulePlan) {
    let toSchedulePlan;
    const fromCycleIndex = get(fromSchedulePlan, 'cycleIndex');
    const fromIterationIndex = get(fromSchedulePlan, 'iterationIndex');
    const fromScheduleEdge = get(fromSchedulePlan, 'scheduleEdge', null);
    const toScheduleEdgeIndex = this.nextEdgeIndex(fromScheduleEdge);
    // Sestaví se seznam stop seřazený od aktuálně přehrávané stopy.
    const { scheduleEdgeArray } = this;
    scheduleEdgeArray
      .slice(toScheduleEdgeIndex)
      .concat(scheduleEdgeArray.slice(0, toScheduleEdgeIndex))
      // Hledá se stopa, která umí naplánovat další slajd. Prázdné se přeskakují.
      .some((scheduleEdge) => {
        const childScheduleStrategy = scheduleEdge.scheduleStrategy;
        const childScheduleInfo = childScheduleStrategy.go();
        if (childScheduleInfo.isEmpty()) {
          childScheduleStrategy.stop();
          return false;
        }
        // Vhodná stopa nalezena. Vrací se její výsledek.
        const toCycleIndex = this.nextCycleIndex(
          fromCycleIndex,
          fromScheduleEdge
        );
        const toIterationIndex = this.nextIterationIndex(
          fromCycleIndex,
          fromIterationIndex,
          toCycleIndex
        );
        toSchedulePlan = {
          cycleIndex: toCycleIndex,
          iterationIndex: toIterationIndex,
          scheduleEdge,
          scheduleInfo: childScheduleInfo.merge({
            cycleIndex: toCycleIndex,
            iterationIndex: toIterationIndex,
            playlistArray: [this.playlist]
          })
        };
        return true;
      });
    if (isNil(toSchedulePlan)) {
      // Pokud se přeskočí všechny stopy, tak se vrací prázdný slajd.
      // TODO #138 Přepracovat přístup k prázdnému `SchedulePlan`.
      // - Přidat příznak? Dovolit `null`? Zachovat hodnoty `cycleIndex` a další?
      toSchedulePlan = null;
    }
    return toSchedulePlan;
  }

  /**
   * Ověří, jestli je hrana poslední v seznamu hran této strategie.
   * @param {ScheduleEdge} scheduleEdge Hrana, pro kterou se má ověřit pořadí.
   * @private
   * @returns {number} Příznak, jestli je hrana poslední.
   */
  isLastEdge(scheduleEdge) {
    const { scheduleEdgeArray } = this;
    const { length } = scheduleEdgeArray;
    return length > 0 && scheduleEdge === scheduleEdgeArray[length - 1];
  }

  /**
   * Určí pořadí cyklu pro následující plán.
   * Uvažuje pouze aktuální úroveň. Nekontroluje obsah vnitřních strategií.
   * @param {null|number} fromCycleIndex Pořadí cyklu, ze kterého se vychází.
   * @param {ScheduleEdge|null} fromScheduleEdge Hrana, ze které se vychází.
   * @private
   * @returns {number} Pořadí cyklu, které má následující plán.
   */
  nextCycleIndex(fromCycleIndex, fromScheduleEdge) {
    if (isNil(fromCycleIndex) || isNil(fromScheduleEdge)) {
      // Bez výchozí stopy se čísluje od nuly.
      return 0;
    }
    if (this.isLastEdge(fromScheduleEdge)) {
      return fromCycleIndex + 1;
    }
    // Určení cyklu aktuální stopy z výchozí.
    return fromCycleIndex;
  }

  /**
   * Určí pořadí hrany pro následující plán.
   * @param {ScheduleEdge|null} fromScheduleEdge Hrana, ze které se vychází.
   * @private
   * @returns {number} Pořadí hrany, které má následující plán.
   */
  nextEdgeIndex(fromScheduleEdge) {
    if (isNil(fromScheduleEdge)) {
      // Bez výchozí stopy se čísluje od nuly.
      return 0;
    }
    const scheduleEdgeIndex = this.findEdgeIndex(fromScheduleEdge);
    return (scheduleEdgeIndex + 1) % this.scheduleEdgeArray.length;
  }

  /**
   * Určí pořadí iterace pro následující plán.
   * @param {null|number} fromCycleIndex Pořadí cyklu, ze kterého se vychází.
   * @param {null|number} fromIterationIndex Pořadí iterace, ze kterého se vychází.
   * @param {number} toCycleIndex Pořadí cyklu, které má následující plán.
   * @private
   * @returns {number} Pořadí iterace, které má následující plán.
   */
  nextIterationIndex(fromCycleIndex, fromIterationIndex, toCycleIndex) {
    if (fromCycleIndex === toCycleIndex) {
      // Pokud se nespustil nový cyklus, tak se hodnota inkrementuje.
      if (isNil(fromIterationIndex)) {
        return 0;
      }
      return fromIterationIndex + 1;
    }
    if (isNil(fromCycleIndex) || fromCycleIndex < toCycleIndex) {
      // Pokud jde o první plán, nebo se cyklus zvýšil, začíná se počítat od nuly.
      return 0;
    }
    throw new Error(
      `Invalid cycle change from ${fromCycleIndex} to ${toCycleIndex}.`
    );
  }

  /** @inheritdoc */
  off(...args) {
    this.emitter.off(...args);
  }

  /** @inheritdoc */
  on(...args) {
    this.emitter.on(...args);
  }

  /**
   * Reakce na přehrání slajdu.
   * @param {ScheduleStrategy} scheduleStrategy Vnitřní strategie, která byla přehrána.
   * @param {ScheduleInfo} scheduleInfo Informace o slajdu, který byl přehrán.
   * @private
   * @returns {undefined} Nic.
   */
  onPlayed(scheduleStrategy, scheduleInfo) {
    const { schedulePlan } = this;
    // Zkontroluje se, že událost přichází z větve aktivní stopy.
    if (scheduleStrategy !== schedulePlan.scheduleEdge.scheduleStrategy) {
      throw new Error(`Unexpected played event from ${scheduleStrategy}.`);
    }
    // Předání události o přehrání výše.
    this.emitter.emit('played', schedulePlan.scheduleInfo);
  }

  /** @inheritdoc */
  plan(offset) {
    if (!Numeric.isIndex(offset)) {
      throw new TypeError(`Invalid offset ${offset}.`);
    }
    const { scheduleEdgeArray, schedulePlan } = this;
    const fromCycleIndex = get(schedulePlan, 'cycleIndex');
    const fromIterationIndex = get(schedulePlan, 'iterationIndex');
    const fromScheduleEdge = get(schedulePlan, 'scheduleEdge', null);
    // Všechny podřazené stopy se seřadí od aktuálně přehrávané po předchozí.
    // V tomto pořadí se budou určovat naplánované informace.
    let sliceIndex;
    if (isNil(fromScheduleEdge)) {
      sliceIndex = 0;
    } else {
      sliceIndex = this.findEdgeIndex(fromScheduleEdge);
    }
    return (
      scheduleEdgeArray
        .slice(sliceIndex)
        .concat(scheduleEdgeArray.slice(0, sliceIndex))
        // Naplánují se informace ze všech stop v požadované délce.
        // Provádí se pouze jeden průchod všech stop a jednorázové určení jejich plánovacích informací.
        // Informace z jednolivých stop se spojí dohromady ve výsledném plánovacím pořadí.
        // Zjednodušená ukázka očekávaný výstupů pro nestedSuite a offset 7:
        // - t3: m0, m0, m0, m0, m0, m0, m0
        // - t6: m1, m1, m1, m1, m1, m1
        // - t7: m2, m2, m2, m2, m2
        // - t4: m1, m2, m1, m2, m1, m2
        // - t5: m3, m3, m3, m3
        // - t0: m0, m1, m2, m3, m0, m1, m2
        // - t1: m4, m4, m4
        // - t6: m1, m1
        // - t7: m2
        // - t2: m1, m2
        // - result: m0, m1, m2, m3, m4, m1, m2
        .reduce(
          (acc, scheduleEdge) => {
            const accCycleIndex = acc.cycleIndex;
            const accOffest = acc.offset;
            const childScheduleInfoArray = scheduleEdge.scheduleStrategy
              .plan(accOffest)
              // TODO #138 Přepracovat přístup k prázdnému `ScheduleInfo`.
              // - Hotfix odfiltrování prázdných slajdů, které nejde setřídit podle `cycleIndex` a `iterationIndex`.
              .filter((childScheduleInfo) => !childScheduleInfo.isEmpty())
              // Prozatím se nahradí pořadí v iteraci za číslo stopy, aby šlo podle něj řadit.
              .map((childScheduleInfo) =>
                childScheduleInfo.merge({
                  iterationIndex: this.findEdgeIndex(scheduleEdge)
                })
              );
            // Najdou se naplánované informace, které odpovídají aktuálnímu cyklu plánovače.
            // Tyto naplánované informace budou najisto použity ve výsledku plánování této úrovně.
            const foundOffset = childScheduleInfoArray.filter(
              (scheduleInfo) => {
                const cycleIndex = scheduleInfo.getCycleIndex();
                if (isNil(accCycleIndex)) {
                  return cycleIndex === 0;
                }
                return cycleIndex === accCycleIndex;
              }
            ).length;
            return {
              cycleIndex: this.nextCycleIndex(accCycleIndex, scheduleEdge),
              // Optimalizace výkonu snížením počtu plánovaných slajdů v dalších stopách.
              // Není je třeba již plánovat na celou délku, protože již požadované výsledky obsadili předchozí stopy.
              offset: accOffest - foundOffset,
              // Sloučí se naplánovaný obsah z různých stop. Dodržuje se pořadí naplánování.
              // Ze sloučeného seznamu se odřízne konec, který přesahuje požadované plánování.
              // Snižuje se tím složitost na zatřízení další stopy.
              scheduleInfoArray: sortBy(
                acc.scheduleInfoArray.concat(childScheduleInfoArray),
                [
                  (scheduleInfo) => scheduleInfo.getCycleIndex(),
                  (scheduleInfo) => scheduleInfo.getIterationIndex()
                ]
              ).slice(0, offset)
            };
          },
          {
            cycleIndex: fromCycleIndex,
            offset,
            scheduleInfoArray: []
          }
        )
        // Nyní jsou známé naplánované informace a jejich cykly.
        // Doplní se pořadí informací, tak jak byly za sebou v cyklech naplánovány.
        // Dále se doplní další informace, které byly v předchozích výpočtech zbytečné (hlasitost).
        .scheduleInfoArray.reduce(
          (acc, scheduleInfo) => {
            const previousScheduleInfo = acc[acc.length - 1];
            let previousCycleIndex;
            if (previousScheduleInfo.hasCycleIndex()) {
              previousCycleIndex = previousScheduleInfo.getCycleIndex();
            } else {
              previousCycleIndex = null;
            }
            let previousIterationIndex;
            if (previousScheduleInfo.hasIterationIndex()) {
              previousIterationIndex = previousScheduleInfo.getIterationIndex();
            } else {
              previousIterationIndex = null;
            }
            const toScheduleInfo = scheduleInfo.merge({
              iterationIndex: this.nextIterationIndex(
                previousCycleIndex,
                previousIterationIndex,
                scheduleInfo.getCycleIndex()
              ),
              playlistArray: [this.playlist]
            });
            return acc.concat(toScheduleInfo);
          },
          [
            new ScheduleInfo({
              cycleIndex: fromCycleIndex,
              iterationIndex: fromIterationIndex
            })
          ]
        )
        .slice(1)
        // TODO #138 Přepracovat přístup k prázdnému `ScheduleInfo`.
        // - Hotfix doplnění prázdnými slajdy, pokud byly nějaké odfiltrovány.
        .concat(Array(offset).fill(new ScheduleInfo()))
        .slice(0, offset)
    );
  }

  /** @inheritdoc */
  skip() {
    // TODO #138 Pokročilé zpracování `#skip`:
    // - Aktuální zpracování není dokonalé, ale vyhovuje požadavkům:
    //   - Může zacyklit, např. pokud by byl nějaký vnitřní playlist nekonečný a nepřešel na další `cycleIndex`.
    //   - Ovlivňuje (někdy!) časovač, což nemusí být úplně očekávané chování při přeskakování cyklu.
    //   - Zatím změna časovačů nevadí, protože se pak vždy volá `#stop`. Pro dopřednou kompatibilitu ponecháno mimo tuto metodu.
    // - Byla zde snaha o zavedení nového algoritmu:
    //   - Všem (nebo jen následujícím) `scheduleEdge` se přepošle informace, že se mají přeskočit.
    //   - Nějakým způsobem (testováno nastavením `scheduleEdge` na poslední) zajistit, aby příští volání `#go` přešlo na následujícíc cyklus.
    // - Nový algoritmus ale přinesl řadu vedlejších problémů:
    //   - U médií je očekáváno, že `#skip` neprovede nic, tím pádem ale zůstanou média v playlistu "zaseknutá" na předchozím cyklu.
    //   - To by tedy vyžadovalo přepracování `#plan` algoritmu, aby tyto "zaseknnutá" média přeskočil, což by ho asi ještě více zesložitilo a zpomalilo.
    //   - Další problém je s časovačem, při vyvolání `played` události na `scheduleEdge`, ze které bylo voláno `#skip`.
    //   - Taková událost by nesplňovala podmíku, že `played` události chodí jen z aktuálního `scheduleEdge`.
    //   - Protože `scheduleEdge` již může být kvůli přeskočení nastaven jinde (na posledním `scheduleEdge`).
    // - Požadavky na algoritmus nejsou aktuálně příliš striktní. Zvážit do budoucna upřesnění:
    //   - Má přeskočení ovlivnit číslování `iterationIndex` v rodičovském playlistu?
    //     - Např. je přijatelný výstup: 0, 1, 4, 5, 8, 19?
    //     - Nebo vždy dodržovat sekvenci: 0, 1, 2, 3, ...
    //   - Nechávat ukazatel na posledním, nebo na `null`? Výkon a nekonečnost vs. výpočet `iterationIndex`.
    //   - Zvážit jestli přidat nějakou hodnotu na výstup (např. poslední `#go`, počet přeskočených...)?
    const fromSchedulePlan = this.schedulePlan;
    if (!isNil(fromSchedulePlan)) {
      while (true) {
        const { scheduleEdge } = this.schedulePlan;
        const { scheduleStrategy } = scheduleEdge;
        const childScheduleInfo = scheduleStrategy.plan(1)[0];
        if (
          this.isLastEdge(scheduleEdge) &&
          (childScheduleInfo.isEmpty() ||
            childScheduleInfo.getCycleIndex() > fromSchedulePlan.cycleIndex)
        ) {
          break;
        } else {
          this.go();
        }
      }
    }
  }

  /** @inheritdoc */
  stop() {
    const { schedulePlan } = this;
    if (schedulePlan) {
      const { scheduleStrategy } = schedulePlan.scheduleEdge;
      scheduleStrategy.stop();
    }
  }

  /** @inheritdoc */
  toString() {
    return `ScheduleLinear {playlist: ${this.playlist}}`;
  }
}

export default ScheduleLinear;
